import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  Add,
  Edit,
  Delete,
  MoreVert,
  Security,
  Update,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';

interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  color: string;
  level: number;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
}

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);

  // Mock data for roles
  const mockRoles: Role[] = [
    { id: 1, name: 'admin', display_name: 'Administrator', description: 'Full system access', color: '#dc2626', level: 100, is_system: true, is_active: true, created_at: '2024-01-01' },
    { id: 2, name: 'manager', display_name: 'Manager', description: 'Management level access', color: '#f59e0b', level: 80, is_system: true, is_active: true, created_at: '2024-01-01' },
    { id: 3, name: 'employee', display_name: 'Employee', description: 'Standard employee access', color: '#3b82f6', level: 50, is_system: true, is_active: true, created_at: '2024-01-01' },
    { id: 4, name: 'viewer', display_name: 'Viewer', description: 'Read-only access', color: '#6b7280', level: 10, is_system: true, is_active: true, created_at: '2024-01-01' },
  ];

  useEffect(() => {
    setRoles(mockRoles);
    setTotalRecords(4);
    setTotalPages(1);
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, role: Role) => {
    setAnchorEl(event.currentTarget);
    setSelectedRole(role);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRole(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRole) return;

    try {
      await apiService.delete(`/api/roles/${selectedRole.id}`);
      setSnackbar({ open: true, message: 'Role deleted successfully', severity: 'success' });
      setRoles(roles.filter(role => role.id !== selectedRole.id));
    } catch (error) {
      console.error('Error deleting role:', error);
      setSnackbar({ open: true, message: 'Failed to delete role', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedRole(null);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRoles(roles.map(role => role.id));
    } else {
      setSelectedRoles([]);
    }
  };

  const handleSelectRole = (roleId: number, checked: boolean) => {
    if (checked) {
      setSelectedRoles([...selectedRoles, roleId]);
    } else {
      setSelectedRoles(selectedRoles.filter(id => id !== roleId));
    }
  };

  return (
    <Box sx={{ 
      p: 3,
      backgroundColor: '#f0f9ff' // Light blue background
    }}>
      <Card sx={{ 
        borderRadius: '12px', 
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        backgroundColor: 'white'
      }}>
        <CardContent sx={{ p: 3 }}>
          {/* Top Controls */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            gap: 2
          }}>
            <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
              <TextField
                placeholder="Search here"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#d1d5db',
                    },
                    '&:hover fieldset': {
                      borderColor: '#9ca3af',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#6b7280' }} />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{
                  borderRadius: '8px',
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  '&:hover': {
                    borderColor: '#2563eb',
                    backgroundColor: '#eff6ff',
                  },
                  px: 3
                }}
              >
                Filter
              </Button>
            </Box>

            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              sx={{
                borderRadius: '8px',
                borderColor: '#3b82f6',
                color: '#3b82f6',
                '&:hover': {
                  borderColor: '#2563eb',
                  backgroundColor: '#eff6ff',
                },
                px: 3
              }}
            >
              Export
            </Button>
          </Box>

          {/* Data Table */}
          <TableContainer component={Paper} sx={{ 
            borderRadius: '8px',
            boxShadow: 'none',
            border: '1px solid #e5e7eb'
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0d9488' }}> {/* Teal background for headers */}
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: 'white', // White text on teal background
                    borderBottom: '1px solid #e5e7eb',
                    py: 2
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={selectedRoles.length === roles.length && roles.length > 0}
                        indeterminate={selectedRoles.length > 0 && selectedRoles.length < roles.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        sx={{ 
                          color: '#3b82f6',
                          '&.Mui-checked': {
                            color: '#3b82f6',
                          },
                        }}
                      />
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        backgroundColor: '#3b82f6', 
                        borderRadius: '2px' 
                      }} />
                      Role Name
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    py: 2
                  }}>
                    Display Name
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    py: 2
                  }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    py: 2
                  }}>
                    Level
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    py: 2
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    py: 2
                  }}>
                    {/* Update button column header */}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id} sx={{ 
                    '&:hover': { backgroundColor: '#f9fafb' },
                    borderBottom: '1px solid #f3f4f6'
                  }}>
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          checked={selectedRoles.includes(role.id)}
                          onChange={(e) => handleSelectRole(role.id, e.target.checked)}
                          sx={{ 
                            color: '#3b82f6',
                            '&.Mui-checked': {
                              color: '#3b82f6',
                            },
                          }}
                        />
                        <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500 }}>
                          {role.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {role.display_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {role.description}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={role.level}
                        size="small"
                        sx={{
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={role.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={role.is_active ? 'success' : 'default'}
                        sx={{
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Update />}
                        sx={{
                          borderRadius: '6px',
                          borderColor: '#0d9488', // Teal color to match design
                          color: '#0d9488', // Teal color to match design
                          fontSize: '0.75rem',
                          px: 2,
                          py: 0.5,
                          '&:hover': {
                            borderColor: '#0f766e',
                            backgroundColor: '#f0fdfa',
                          },
                        }}
                      >
                        Update
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Footer */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mt: 3,
            pt: 2,
            borderTop: '1px solid #e5e7eb'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Showing
              </Typography>
              <FormControl size="small" sx={{ minWidth: 60 }}>
                <Select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#d1d5db',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#9ca3af',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Showing 1 to 4 out of 4 records
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={true}
                sx={{
                  minWidth: 32,
                  height: 32,
                  borderRadius: '6px',
                  borderColor: '#e5e7eb',
                  color: '#d1d5db',
                }}
              >
                ←
              </Button>
              
              <Button
                variant="contained"
                size="small"
                sx={{
                  minWidth: 32,
                  height: 32,
                  borderRadius: '6px',
                  borderColor: '#d1d5db',
                  color: 'white',
                  backgroundColor: '#0d9488', // Teal for active page
                  '&:hover': {
                    backgroundColor: '#0f766e',
                  },
                }}
              >
                1
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                disabled={true}
                sx={{
                  minWidth: 32,
                  height: 32,
                  borderRadius: '6px',
                  borderColor: '#e5e7eb',
                  color: '#d1d5db',
                }}
              >
                →
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete role "{selectedRole?.display_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleManagement;